import type React from 'react';
import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus, BannerLayout } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Input, version2 } from 'ui-components/components/input';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import { useOnComponentMount } from '../../../../../hooks/use-on-component-mount';
import type { WorkflowStep, WorkflowEntityType, WorkflowActionDraft } from '../../types';
import { FilterSection } from './filter-section';
import { ActionSection } from './action-section';
import { messages } from './messages';
import styles from './styles.module.less';

// TODO: Very rough cut at rule step configuration. Needs to be able to handle a rule with no filters
// or an order filter reference.
const calculateStepConfigured = (title: string, actions: WorkflowActionDraft[] | undefined): boolean =>
  title.trim().length > 0 && (actions?.length ?? 0) > 0;

type StepEditorProps = {
  step: WorkflowStep;
  entityType: WorkflowEntityType;
  onUpdateStep: (step: WorkflowStep) => void;
};

export const StepEditor: React.FC<StepEditorProps> = ({ step, entityType, onUpdateStep }) => {
  const intl = useIntl();
  const titleInputRef = useRef<HTMLInputElement>(null);

  useOnComponentMount(() => {
    if (titleInputRef.current && !step.title) {
      titleInputRef.current.focus();
    }
  });

  const incompleteCount = useMemo(() => {
    const incompleteFilters = (step.filters ?? []).filter(
      (f) => f.fieldName === '' || f.operator === '' || (Array.isArray(f.value) ? f.value.length === 0 : f.value === ''),
    ).length;
    const incompleteActions = (step.actions ?? []).filter((a) => a.actionTypeId === 0).length;
    return incompleteFilters + incompleteActions;
  }, [step.filters, step.actions]);

  if (step.type !== 'action') {
    return null;
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    onUpdateStep({
      ...step,
      title: newTitle,
      isConfigured: calculateStepConfigured(newTitle, step.actions),
    });
  };

  const handleStepUpdate = (updatedStep: WorkflowStep) => {
    onUpdateStep({
      ...updatedStep,
      isConfigured: calculateStepConfigured(updatedStep.title, updatedStep.actions),
    });
  };

  return (
    <FlexColumn className={styles.editor} gap={Gap.Space16}>
      {incompleteCount > 0 && (
        <BannerMessageV2
          content={
            <Text textType={TextType.Caption} value={intl.formatMessage(messages.incompleteConditionsBanner, { count: incompleteCount })} />
          }
          layout={BannerLayout.Inline}
          status={BannerStatus.Info}
        />
      )}
      <FlexColumn gap={Gap.Space16}>
        <Text textType={TextType.Label} value={messages.stepTitleLabel} />
        <Input
          ref={titleInputRef}
          onChange={handleTitleChange}
          placeholder={intl.formatMessage(messages.stepTitlePlaceholder)}
          value={step.title}
          version={version2}
        />
      </FlexColumn>

      <FlexColumn gap={Gap.Space16}>
        <Text textType={TextType.H4} value={messages.filtersHeader} />
        <FilterSection entityType={entityType} onUpdateStep={handleStepUpdate} step={step} />
      </FlexColumn>

      <FlexColumn gap={Gap.Space16}>
        <Text textType={TextType.H4} value={messages.actionsHeader} />
        <ActionSection entityType={entityType} onUpdateStep={handleStepUpdate} step={step} />
      </FlexColumn>
    </FlexColumn>
  );
};
